import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment';
import { TranslatorService } from '../../../../services/translator_service';
import { ApiQuery } from '../../../../web-services/api/api.query';
import { ApiService } from '../../../../web-services/api/api.service';

export interface MonthRange {
  start: { year: number; month: number } | null;
  end:   { year: number; month: number } | null;
}

interface SiteGroup {
  letter: string;
  names: any[];
  display: boolean;
}

@Component({
  selector: 'app-invoice-filter-bar',
  imports: [CommonModule, TranslatePipe, MatMenuModule, MatCheckboxModule],
  templateUrl: './invoice-filter-bar.html',
  styleUrl: './invoice-filter-bar.scss',
})
export class InvoiceFilterBar implements OnInit, OnChanges {
  @Input()  eventTypes: string[]       = [];
  @Input()  invoiceSiteNames: string[] = [];
  @Output() monthRangeChange      = new EventEmitter<MonthRange>();
  @Output() siteFilterChange      = new EventEmitter<string[]>();
  @Output() eventTypeFilterChange = new EventEmitter<string[]>();

  @ViewChild('monthTrigger') monthMenuTrigger!: MatMenuTrigger;
  @ViewChild('sitesTrigger') sitesMenuTrigger!: MatMenuTrigger;
  @ViewChild('eventTrigger') eventMenuTrigger!: MatMenuTrigger;

  constructor(
    private translator: TranslatorService,
    private apiQuery: ApiQuery,
    private apiService: ApiService
  ) {}

  currentLang: string = '';

  ngOnInit(): void {
    this.translator.currentLangEmitter$.subscribe(value => {
      this.currentLang = value as string;
      this.months = this.getMonthNames(value as string);
    });

    this.monthRangeChange.emit({ start: this.startMonth, end: this.endMonth });

    this.apiService.getSitesList(null);
    this.apiQuery.allSitesListData$.subscribe((sitesList: any[]) => {
      if (!sitesList || sitesList.length === 0) return;
      this.fullSitesList = sitesList;
      this.buildSitesHierarchy(sitesList);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoiceSiteNames'] && this.fullSitesList.length) {
      this.buildSitesHierarchy(this.fullSitesList);
    }
  }

  // Month range

  months: string[] = [];

  private getMonthNames(langCode: string): string[] {
    const locale = langCode === 'iw' ? 'he' : langCode;
    return Array.from({ length: 12 }, (_, i) =>
      moment({ year: 2000, month: i }).locale(locale).format('MMM')
    );
  }

  displayYear = moment().year();
  startMonth: { year: number; month: number } | null = { year: moment().year(), month: 0 };
  endMonth:   { year: number; month: number } | null = { year: moment().year(), month: moment().month() };
  selecting: 'start' | 'end' = 'start';

  get monthLabel(): string {
    if (!this.startMonth) return '';
    const fmt = (m: { year: number; month: number }) =>
      `${this.months[m.month]} ${m.year}`;
    if (this.endMonth) return `${fmt(this.startMonth)} - ${fmt(this.endMonth)}`;
    return fmt(this.startMonth);
  }

  prevYear(): void { this.displayYear--; }
  nextYear(): void { this.displayYear++; }

  selectMonth(i: number): void {
    const m = { year: this.displayYear, month: i };
    if (this.selecting === 'start') {
      this.startMonth = m;
      this.endMonth   = null;
      this.selecting  = 'end';
    } else {
      if (this.monthBefore(m, this.startMonth!)) {
        this.endMonth   = { ...this.startMonth! };
        this.startMonth = m;
      } else {
        this.endMonth = m;
      }
      this.selecting = 'start';
    }
  }

  isStart(i: number): boolean { return this.same({ year: this.displayYear, month: i }, this.startMonth); }
  isEnd(i: number): boolean   { return this.same({ year: this.displayYear, month: i }, this.endMonth); }

  isInRange(i: number): boolean {
    if (!this.startMonth || !this.endMonth) return false;
    const m = { year: this.displayYear, month: i };
    return this.monthAfter(m, this.startMonth) && this.monthBefore(m, this.endMonth);
  }

  clearMonthFilter(event: Event): void {
    event.stopPropagation();
    this.startMonth = null;
    this.endMonth   = null;
    this.selecting  = 'start';
    this.monthRangeChange.emit({ start: null, end: null });
  }

  applyFilter(): void {
    this.monthRangeChange.emit({ start: this.startMonth, end: this.endMonth });
    this.monthMenuTrigger.closeMenu();
  }

  private same(a: { year: number; month: number }, b: { year: number; month: number } | null): boolean {
    return !!b && a.year === b.year && a.month === b.month;
  }
  private monthBefore(a: { year: number; month: number }, b: { year: number; month: number }): boolean {
    return a.year < b.year || (a.year === b.year && a.month < b.month);
  }
  private monthAfter(a: { year: number; month: number }, b: { year: number; month: number }): boolean {
    return a.year > b.year || (a.year === b.year && a.month > b.month);
  }

  // Sites filter

  sitesHierarchy: SiteGroup[] = [];
  selectedSiteIds: number[] = [];
  private fullSitesList: any[] = [];

  get sitesLabel(): string {
    if (this.selectedSiteIds.length === 0) return '';
    if (this.selectedSiteIds.length === 1) {
      for (const group of this.sitesHierarchy) {
        const found = group.names.find(s => s.site_id === this.selectedSiteIds[0]);
        if (found) return found.site_name;
      }
    }
    return `${this.selectedSiteIds.length}`;
  }

  private buildSitesHierarchy(sitesList: any[]): void {
    // Only include sites that are in the invoice list (when invoiceSiteNames is populated)
    const filtered = this.invoiceSiteNames.length > 0
      ? sitesList.filter(s => this.invoiceSiteNames.includes(s.site_name))
      : [];

    this.sitesHierarchy = [];
    for (const site of filtered) {
      if (site.linked_sites_name) {
        const existing = this.sitesHierarchy.find(g => g.letter === site.linked_sites_name);
        if (existing) {
          existing.names.push(site);
        } else {
          this.sitesHierarchy.push({ letter: site.linked_sites_name, names: [site], display: true });
        }
      } else {
        this.sitesHierarchy.push({ letter: '', names: [site], display: true });
      }
    }
  }

  filterSites(text: string): void {
    const q = text.toLowerCase();
    for (const group of this.sitesHierarchy) {
      group.display = !q ||
        group.letter.toLowerCase().includes(q) ||
        group.names.some(s => s.site_name.toLowerCase().includes(q));
    }
  }

  isSiteSelected(site: any): boolean { return this.selectedSiteIds.includes(site.site_id); }

  isGroupSelected(group: SiteGroup): boolean {
    return group.names.length > 0 && group.names.every(s => this.selectedSiteIds.includes(s.site_id));
  }

  isGroupIndeterminate(group: SiteGroup): boolean {
    const count = group.names.filter(s => this.selectedSiteIds.includes(s.site_id)).length;
    return count > 0 && count < group.names.length;
  }

  toggleSite(site: any): void {
    if (this.selectedSiteIds.includes(site.site_id)) {
      this.selectedSiteIds = this.selectedSiteIds.filter(id => id !== site.site_id);
    } else {
      this.selectedSiteIds = [...this.selectedSiteIds, site.site_id];
    }
  }

  toggleGroup(group: SiteGroup): void {
    if (this.isGroupSelected(group)) {
      this.selectedSiteIds = this.selectedSiteIds.filter(id => !group.names.some(s => s.site_id === id));
    } else {
      const newIds = group.names.map(s => s.site_id).filter(id => !this.selectedSiteIds.includes(id));
      this.selectedSiteIds = [...this.selectedSiteIds, ...newIds];
    }
  }

  clearSiteFilter(event: Event): void {
    event.stopPropagation();
    this.selectedSiteIds = [];
    this.siteFilterChange.emit([]);
  }

  applySiteFilter(): void {
    // Emit site names so invoices component can filter by name
    const selectedNames = this.fullSitesList
      .filter(s => this.selectedSiteIds.includes(s.site_id))
      .map(s => s.site_name);
    this.siteFilterChange.emit(selectedNames);
    this.sitesMenuTrigger.closeMenu();
  }

  // Event type filter

  selectedEventTypes: string[] = [];

  get eventTypeLabel(): string {
    if (this.selectedEventTypes.length === 0) return '';
    if (this.selectedEventTypes.length === 1) return this.selectedEventTypes[0];
    return `${this.selectedEventTypes.length}`;
  }

  isEventTypeSelected(type: string): boolean { return this.selectedEventTypes.includes(type); }

  toggleEventType(type: string): void {
    if (this.selectedEventTypes.includes(type)) {
      this.selectedEventTypes = this.selectedEventTypes.filter(t => t !== type);
    } else {
      this.selectedEventTypes = [...this.selectedEventTypes, type];
    }
  }

  clearEventTypeFilter(event: Event): void {
    event.stopPropagation();
    this.selectedEventTypes = [];
    this.eventTypeFilterChange.emit([]);
  }

  applyEventTypeFilter(): void {
    this.eventTypeFilterChange.emit([...this.selectedEventTypes]);
    this.eventMenuTrigger.closeMenu();
  }

  // Clear all

  get hasAnyFilter(): boolean {
    return this.selectedSiteIds.length > 0 || this.selectedEventTypes.length > 0;
  }

  clearAll(): void {
    this.selectedSiteIds = [];
    this.siteFilterChange.emit([]);
    this.selectedEventTypes = [];
    this.eventTypeFilterChange.emit([]);
  }
}
